import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster, toast } from "@/components/ui/sonner";
import { SiteShell } from "@/components/SiteShell";
import { GlossaryPage } from "@/pages/GlossaryPage";
import { AtlasPage } from "@/pages/AtlasPage";
import { ArchivePage } from "@/pages/ArchivePage";
import { VipRoute } from "@/components/VipRoute";
import { VipAccountPage } from "@/pages/VipAccountPage";
import { VipChatPage } from "@/pages/VipChatPage";
import { VipReturnPage } from "@/pages/VipReturnPage";
import { CommunityPage } from "@/pages/CommunityPage";
import { AchievementsPage } from "@/pages/AchievementsPage";
import { LeaderboardPage } from "@/pages/LeaderboardPage";
import { TimelinePage } from "@/pages/TimelinePage";
import { OraclePopupPage } from "@/pages/OraclePopupPage";
import { CorrespondencesPage } from "@/pages/CorrespondencesPage";
import { InitiationPage } from "@/pages/InitiationPage";
import { FloatingOracle } from "@/components/FloatingOracle";
import HomePage from "@/pages/HomePage";
import StructurePage from "@/pages/StructurePage";
import { checkVipAccess, fetchSystemSummary } from "@/lib/api";

function App() {
  const [language, setLanguage] = useState("en");
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");
  const [vipEmail, setVipEmail] = useState(localStorage.getItem("anatomia-vip-email") || "");
  const [showFloatingOracle, setShowFloatingOracle] = useState(false);
  const [vipState, setVipState] = useState({
    loading: true,
    access_granted: false,
    status: "unpaid",
    access_expires_at: "",
    latest_session_id: "",
  });

  useEffect(() => {
    const loadSummary = async () => {
      try {
        setSummaryLoading(true);
        const data = await fetchSystemSummary();
        setSummary(data);
        setSummaryError("");
      } catch (error) {
        console.error(error);
        setSummaryError("Unable to load the temple summary right now.");
        toast.error("The temple archive could not load.");
      } finally {
        setSummaryLoading(false);
      }
    };

    loadSummary();
  }, []);

  useEffect(() => {
    const hydrateVipAccess = async () => {
      if (!vipEmail) {
        setVipState((current) => ({ ...current, loading: false, access_granted: false, status: "unpaid" }));
        return;
      }

      try {
        setVipState((current) => ({ ...current, loading: true }));
        const data = await checkVipAccess({ email: vipEmail });
        if (data.access_granted) {
          setVipState({ loading: false, ...data });
          localStorage.setItem("anatomia-vip-email", vipEmail);
        } else {
          setVipState({ loading: false, ...data });
        }
      } catch (error) {
        console.error(error);
        setVipState((current) => ({ ...current, loading: false }));
      }
    };

    hydrateVipAccess();
  }, [vipEmail]);

  const handleVipAccessResolved = (email, accessData) => {
    const normalizedEmail = email.trim().toLowerCase();
    setVipEmail(normalizedEmail);
    localStorage.setItem("anatomia-vip-email", normalizedEmail);
    setVipState({ loading: false, ...accessData });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <BrowserRouter>
        <SiteShell
          language={language}
          onLanguageChange={setLanguage}
          summary={summary}
          summaryLoading={summaryLoading}
          summaryError={summaryError}
          vipEmail={vipEmail}
          vipState={vipState}
        >
          <Routes>
            <Route
              path="/"
              element={
                <HomePage
                  language={language}
                  summary={summary}
                  summaryLoading={summaryLoading}
                  vipAccess={vipState.access_granted}
                />
              }
            />
            <Route
              path="/atlas"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <AtlasPage language={language} vipEmail={vipEmail} />
                </VipRoute>
              }
            />
            <Route
              path="/archive"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <ArchivePage language={language} />
                </VipRoute>
              }
            />
            <Route
              path="/structure"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <StructurePage language={language} summary={summary} />
                </VipRoute>
              }
            />
            <Route
              path="/glossary"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <GlossaryPage language={language} />
                </VipRoute>
              }
            />
            <Route
              path="/vip-chat"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <VipChatPage language={language} vipEmail={vipEmail} />
                </VipRoute>
              }
            />
            <Route
              path="/vip-account"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <VipAccountPage language={language} vipEmail={vipEmail} />
                </VipRoute>
              }
            />
            <Route
              path="/vip-return"
              element={<VipReturnPage language={language} onAccessResolved={handleVipAccessResolved} />}
            />
            <Route
              path="/community"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <CommunityPage language={language} vipEmail={vipEmail} />
                </VipRoute>
              }
            />
            <Route
              path="/achievements"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <AchievementsPage language={language} vipEmail={vipEmail} />
                </VipRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <LeaderboardPage language={language} vipEmail={vipEmail} />
                </VipRoute>
              }
            />
            <Route
              path="/timeline"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <TimelinePage language={language} vipEmail={vipEmail} />
                </VipRoute>
              }
            />
            <Route
              path="/correspondences"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <CorrespondencesPage language={language} vipEmail={vipEmail} />
                </VipRoute>
              }
            />
            <Route
              path="/initiation"
              element={
                <VipRoute access={vipState.access_granted} language={language} onAccessResolved={handleVipAccessResolved} vipEmail={vipEmail}>
                  <InitiationPage language={language} vipEmail={vipEmail} />
                </VipRoute>
              }
            />
            <Route
              path="/oracle-popup"
              element={<OraclePopupPage language={language} />}
            />
          </Routes>
        </SiteShell>
        
        {/* Floating Oracle */}
        {vipState.access_granted && showFloatingOracle && (
          <FloatingOracle
            language={language}
            vipEmail={vipEmail}
            onClose={() => setShowFloatingOracle(false)}
          />
        )}
        
        {/* Floating Oracle Toggle Button */}
        {vipState.access_granted && !showFloatingOracle && (
          <button
            className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full border-2 border-primary/50 bg-gradient-to-br from-primary/20 to-primary/5 shadow-lg shadow-primary/20 transition-all hover:scale-110 hover:border-primary hover:shadow-primary/40"
            data-testid="floating-oracle-toggle"
            onClick={() => setShowFloatingOracle(true)}
            title="Open Kōnshus Oracle"
          >
            <span className="text-2xl">🔮</span>
          </button>
        )}
        
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
